import { type FC } from 'react';

import { useDataOrchestrator } from '@/hooks/useDataFetch';
import { WorldMap } from '@/components/map/WorldMap';
import { PanelGrid } from '@/components/layout/PanelGrid';
import { StatusBar } from '@/components/layout/StatusBar';
import { LayerControls } from '@/components/map/LayerControls';
import { TacticalHUD } from '@/components/map/TacticalHUD';
import { SensorRail } from '@/components/map/SensorRail';
import { AircraftDetailCard } from '@/components/map/AircraftDetailCard';
import { CctvPlayer } from '@/components/map/CctvPlayer';
import { CrisisAlertBanner } from '@/components/ui/CrisisAlertBanner';
import { CountryProfileCard } from '@/components/panels/CountryProfileCard';
import { CountryCompareModal } from '@/components/panels/CountryCompareModal';
import { BreakingAlertOverlay } from '@/components/ui/BreakingAlertOverlay';
import { SimulationModal } from '@/components/simulation/SimulationModal';
import { useAppStore } from '@/stores/app-store';

const MapZoomControls: FC = () => {
  const viewState = useAppStore((s) => s.viewState);
  const setViewState = useAppStore((s) => s.setViewState);

  const zoomIn  = () => setViewState({ ...viewState, zoom: Math.min((viewState.zoom ?? 2) + 1, 20) });
  const zoomOut = () => setViewState({ ...viewState, zoom: Math.max((viewState.zoom ?? 2) - 1, 0.5) });

  return (
    <div className="absolute bottom-24 md:bottom-12 z-30 flex flex-col gap-0.5 right-4 md:right-[43%] lg:right-[39%]">
      <button
        onClick={zoomIn}
        className="w-7 h-7 flex items-center justify-center rounded-t bg-surface-panel/90 border border-surface-border text-slate-300 hover:text-white hover:bg-slate-700 transition-colors text-sm font-bold shadow-lg"
        title="Zoom In"
      >+</button>
      <button
        onClick={zoomOut}
        className="w-7 h-7 flex items-center justify-center rounded-b bg-surface-panel/90 border border-surface-border text-slate-300 hover:text-white hover:bg-slate-700 transition-colors text-sm font-bold shadow-lg"
        title="Zoom Out"
      >−</button>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Main dashboard orchestrator
// ---------------------------------------------------------------------------


export const Dashboard: FC = () => {
  useDataOrchestrator();
  const sensorMode = useAppStore((s) => s.sensorMode);

  return (
    <div className="relative flex-1 overflow-hidden">
      {/* Full-bleed map background */}
      <WorldMap />

      {/* 지도 위 극장 효과 — 센서 광학이 꺼져 있을 때만 (셰이더가 자체 비네팅을 갖는다) */}
      {sensorMode === 'none' && (
        <>
          <div
            className="absolute inset-0 z-[23] pointer-events-none"
            style={{ background: 'radial-gradient(ellipse at center, transparent 55%, rgba(8,13,20,0.55) 100%)' }}
          />
          <div className="scanlines absolute inset-0 z-[24] pointer-events-none" />
        </>
      )}

      {/* Tactical HUD — 레티클/베어링/좌표 리드아웃 */}
      <TacticalHUD />

      {/* Sensor Rail — 광학 · 이미저리 · 뷰 토글 (하단 커맨드 독) */}
      <SensorRail />

      {/* 항공기 클릭 시 실시간 추적 카드 */}
      <AircraftDetailCard />

      {/* 교통 CCTV 클릭 시 HLS 플레이어 */}
      <CctvPlayer />

      {/* Breaking Alert Overlay (top, highest priority) */}
      <BreakingAlertOverlay />

      {/* Crisis Alert Banner (top-left, above everything) */}
      <CrisisAlertBanner />

      {/* Layer Controls (bottom-left) */}
      <LayerControls />

      {/* Map Zoom +/- (bottom-right of map area) */}
      <MapZoomControls />

      {/* Country Profile Card (floats above LayerControls on click) */}
      <CountryProfileCard />

      {/* Country Compare Modal (full modal when two countries selected) */}
      <CountryCompareModal />

      {/* Panel overlay (right side) — always visible, panels handle own loading state */}
      <PanelGrid />

      {/* Bottom status bar */}
      <div className="absolute bottom-0 left-0 right-0">
        <StatusBar />
      </div>

      {/* Simulation Mode (full-screen, z-200) */}
      <SimulationModal />
    </div>
  );
};
